import { defineStore } from 'pinia'
import type { LibraryImpactOfDisability } from '@/library/library_types'
import { IOD_SAVING_STATES } from './impact-of-disability_constants'
import type { SavingState, GeneratedStatement } from './impact-of-disability_types'
import { getImpactOfDisabilityStatement, updateImpactOfDisabilityStatement } from './api'

// Cache key format: "disability|subject|impactArea|contentProfile"
function createCacheKey(disability: string, subject: string, impactArea: string, contentProfile?: string): string {
  return contentProfile 
    ? `${disability}|${subject}|${impactArea}|${contentProfile}`
    : `${disability}|${subject}|${impactArea}`
}

export const useImpactOfDisabilityStore = defineStore('impactOfDisability', {
  state: () => ({
    savedStatements: [] as LibraryImpactOfDisability[],
    savingStates: {} as Record<string, SavingState>,
    generatedStatementsCache: {} as Record<string, GeneratedStatement>,
    // Note: customPromptCache is intentionally coarse-grained (keyed by disability|contentProfile only).
    // The is_custom_prompt flag is determined per API request (not per statement), so all statements
    // from the same generation batch share the same flag value. This design is sufficient for the
    // current API behavior where custom prompt usage applies to the entire generation request.
    customPromptCache: {} as Record<string, boolean>
  }),
  
  actions: {
    async fetchStatementByUUID(uuid: string) {
      try {
        const data = await getImpactOfDisabilityStatement(uuid)
        
        // Update or add the statement to the store
        const existingIndex = this.savedStatements.findIndex(s => s.uuid === uuid)
        if (existingIndex >= 0) {
          this.savedStatements[existingIndex] = data
        } else {
          this.savedStatements.push(data)
        }
        
        return data
      } catch (error) {
        console.error('Error fetching IOD statement:', error)
        throw error
      }
    },
    
    async saveStatement(uuid: string, fieldsToUpdate: Partial<LibraryImpactOfDisability>) {
      this.setSavingState(uuid, IOD_SAVING_STATES.SAVING)
      
      try {
        const updatedStatement = await updateImpactOfDisabilityStatement(uuid, fieldsToUpdate)
        
        // Update the statement in the store
        const existingIndex = this.savedStatements.findIndex(s => s.uuid === uuid)
        if (existingIndex >= 0) {
          this.savedStatements[existingIndex] = updatedStatement
        }
        
        this.setSavingState(uuid, IOD_SAVING_STATES.JUST_SAVED)
        
        // Reset to NO_CHANGES after a delay
        setTimeout(() => {
          this.setSavingState(uuid, IOD_SAVING_STATES.NO_CHANGES)
        }, 3000)
        
        return updatedStatement
      } catch (error) {
        console.error('Error saving IOD statement:', error)
        this.setSavingState(uuid, IOD_SAVING_STATES.UNSAVED_CHANGES)
        throw error
      }
    },
    
    updateStatement(statement: LibraryImpactOfDisability) {
      const existingIndex = this.savedStatements.findIndex(s => s.uuid === statement.uuid)
      if (existingIndex >= 0) {
        this.savedStatements[existingIndex] = { ...statement }
      }
    },
    
    setSavingState(uuid: string, state: SavingState) {
      this.savingStates[uuid] = state
    },
    
    // Cache generated statements by disability + subject + impact area + content profile
    cacheGeneratedStatements(disability: string, statements: GeneratedStatement[], contentProfile?: string, isCustomPrompt?: boolean) {
      statements.forEach(statement => {
        const key = createCacheKey(disability, statement.subject, statement.impact_area, contentProfile)
        this.generatedStatementsCache[key] = statement
      })
      // Cache the custom prompt flag separately (keyed by disability + content profile only).
      // This is coarse-grained intentionally: is_custom_prompt is determined per API request,
      // so all statements from the same generation batch share the same flag value.
      if (isCustomPrompt !== undefined) {
        const customPromptKey = contentProfile ? `${disability}|${contentProfile}` : disability
        this.customPromptCache[customPromptKey] = isCustomPrompt
      }
    },

    // Get cached custom prompt flag for a disability + content profile.
    // Note: This returns the coarse-grained flag that applies to all statements for this disability/contentProfile.
    getCachedCustomPrompt(disability: string, contentProfile?: string): boolean | undefined {
      const customPromptKey = contentProfile ? `${disability}|${contentProfile}` : disability
      return this.customPromptCache[customPromptKey]
    },
    
    // Get cached statement for a specific combination
    getCachedStatement(disability: string, subject: string, impactArea: string, contentProfile?: string): GeneratedStatement | undefined {
      const key = createCacheKey(disability, subject, impactArea, contentProfile)
      return this.generatedStatementsCache[key]
    },
    
    // Check if all requested combinations are cached
    areAllStatementsCached(disability: string, subjects: string[], impactAreas: string[], contentProfile?: string): boolean {
      for (const subject of subjects) {
        for (const impactArea of impactAreas) {
          const key = createCacheKey(disability, subject, impactArea, contentProfile)
          if (!this.generatedStatementsCache[key]) {
            return false
          }
        }
      }
      return true
    },
    
    // Get all cached statements for the requested combinations
    getCachedStatements(disability: string, subjects: string[], impactAreas: string[], contentProfile?: string): GeneratedStatement[] {
      const cached: GeneratedStatement[] = []
      for (const subject of subjects) {
        for (const impactArea of impactAreas) {
          const statement = this.getCachedStatement(disability, subject, impactArea, contentProfile)
          if (statement) {
            cached.push(statement)
          }
        }
      }
      return cached
    },
    
    // Get missing combinations that need to be generated
    getMissingCombinations(disability: string, subjects: string[], impactAreas: string[], contentProfile?: string): { subjects: string[], impactAreas: string[] } {
      const missingSubjects = new Set<string>()
      const missingImpactAreas = new Set<string>()
      
      for (const subject of subjects) {
        for (const impactArea of impactAreas) {
          const key = createCacheKey(disability, subject, impactArea, contentProfile)
          if (!this.generatedStatementsCache[key]) {
            missingSubjects.add(subject)
            missingImpactAreas.add(impactArea)
          }
        }
      }
      
      return {
        subjects: Array.from(missingSubjects),
        impactAreas: Array.from(missingImpactAreas)
      }
    },
    
    // Clear cache when disability changes
    clearCache() {
      this.generatedStatementsCache = {}
      this.customPromptCache = {}
    }
  }
})
